import React, { useState, useEffect, useContext, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import LoadingContext from '../../context/Loading/Loading'

import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Dropdown from 'react-bootstrap/Dropdown'

import AuthService from '../../utilities/services/auth.service'
import UserContext from '../../context/User/User'
import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'
import { checkPermission } from '../../utilities/helpers'
import {
  getFnbItems,
  getFnbCategories,
  getFnbLocations,
  getOrgVenues,
  updateFnbItem,
  createFnbItem,
} from '../../utilities/api'

import { LoadingContainer } from '../LoadingContainer'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { NoPermissionsContainer } from '../NoPermissionsContainer'

import '../BuyerTypesWrapper/buyerTypesWrapper.scss'
import '../MarketplaceWrapper/marketplace.scss'

function formatCurrency(value) {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
  }).format(Number(value ?? 0))
}

function calcMargin(price, cost) {
  const p = Number(price ?? 0)
  const c = Number(cost ?? 0)
  if (p <= 0) return 0
  return Math.round(((p - c) / p) * 100)
}

function getRowAttrs(row) {
  return row.attributes ?? row
}

function getFnbImageUrl(attrs) {
  const media = attrs?.imageUrl
  if (!media) return ''
  if (typeof media === 'string') return media
  const mediaAttrs = media?.data?.attributes ?? media
  return (
    mediaAttrs?.formats?.thumbnail?.url ??
    mediaAttrs?.formats?.small?.url ??
    mediaAttrs?.url ??
    media?.url ??
    ''
  )
}

function resolveMediaUrl(url) {
  if (!url) return ''
  if (url.startsWith('http')) return url
  if (url.startsWith('/')) {
    const base = process.env.REACT_APP_API?.replace(/\/api\/?$/, '') || ''
    return `${base}${url}`
  }
  return url
}

function hasMarketplacePermission(getPermissions, orgPermissionsReady) {
  if (!orgPermissionsReady) return false
  const perms = getPermissions()
  return (
    checkPermission(perms, ORGANIZATION_PERMISSION_SLUGS.MANAGE_MARKETPLACE) ||
    checkPermission(perms, ORGANIZATION_PERMISSION_SLUGS.EVENT_MANAGEMENT)
  )
}

function exportItemsCsv(items, categoriesById, locationsById, venueNameByUuid) {
  const headers = [
    'Name',
    'SKU',
    'Category',
    'Venue',
    'Price',
    'Cost',
    'Margin %',
    'Status',
    'Locations',
  ]
  const rows = items.map((row) => {
    const a = getRowAttrs(row)
    const category = categoriesById[a.categoryId]?.name ?? ''
    const venue = a.venue_uuid
      ? (venueNameByUuid.get(a.venue_uuid) ?? a.venue_uuid)
      : 'All venues'
    const margin = calcMargin(a.price, a.cost)
    const status = a.isActive ? 'Active' : 'Inactive'
    let locations = 'All locations'
    if (!a.availableAtAllLocations && Array.isArray(a.locationIds)) {
      locations = a.locationIds
        .map((id) => locationsById[id]?.name ?? id)
        .join('; ')
    }
    return [
      a.name,
      a.sku,
      category,
      venue,
      a.price,
      a.cost,
      margin,
      status,
      locations,
    ]
  })
  const csv = [headers, ...rows]
    .map((line) =>
      line
        .map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`)
        .join(',')
    )
    .join('\n')
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'fnb-items.csv'
  link.click()
  URL.revokeObjectURL(url)
}

export default function MarketplaceItemsWrapper() {
  const navigate = useNavigate()
  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)
  const { orgPermissions } = useContext(UserContext)
  const { getPermissions } = AuthService

  const orgUUID = AuthService.getOrg()[0]?.uuid
  const orgPermissionsReady = orgPermissions?.length > 0
  const hasPermission = hasMarketplacePermission(
    getPermissions,
    orgPermissionsReady
  )

  const [items, setItems] = useState([])
  const [categories, setCategories] = useState([])
  const [locations, setLocations] = useState([])
  const [venues, setVenues] = useState([])
  const [isLoadingData, setIsLoadingData] = useState(true)
  const [search, setSearch] = useState('')

  const requiresVenueSelection = venues.length > 1

  const venueNameByUuid = useMemo(() => {
    const map = new Map()
    venues.forEach((venue) => {
      if (venue?.uuid) {
        map.set(venue.uuid, venue.name ?? venue.uuid)
      }
    })
    return map
  }, [venues])

  const categoriesById = useMemo(() => {
    const map = {}
    categories.forEach((row) => {
      const a = getRowAttrs(row)
      map[String(row.id)] = a
    })
    return map
  }, [categories])

  const locationsById = useMemo(() => {
    const map = {}
    locations.forEach((row) => {
      const a = getRowAttrs(row)
      map[String(row.id)] = a
    })
    return map
  }, [locations])

  const load = () => {
    setIsLoadingData(true)
    Promise.all([
      getFnbItems(orgUUID),
      getFnbCategories(orgUUID),
      getFnbLocations(orgUUID),
      getOrgVenues(orgUUID),
    ])
      .then(([itemsRes, categoriesRes, locationsRes, venuesRes]) => {
        setItems(itemsRes?.data?.data ?? [])
        setCategories(categoriesRes?.data?.data ?? [])
        setLocations(locationsRes?.data?.data ?? [])
        setVenues(Array.isArray(venuesRes?.data) ? venuesRes.data : [])
      })
      .catch((err) => console.error(err))
      .finally(() => {
        setIsLoadingData(false)
        hideLoading()
      })
  }

  useEffect(() => {
    if (!orgPermissionsReady) return
    if (!hasPermission) {
      hideLoading()
      return
    }
    if (!orgUUID) return
    showLoading()
    load()
  }, [orgPermissionsReady, hasPermission, orgUUID])

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return items
    return items.filter((row) => {
      const a = getRowAttrs(row)
      const categoryName = categoriesById[a.categoryId]?.name ?? ''
      return (
        String(a.name ?? '')
          .toLowerCase()
          .includes(q) ||
        String(a.sku ?? '')
          .toLowerCase()
          .includes(q) ||
        categoryName.toLowerCase().includes(q)
      )
    })
  }, [items, search, categoriesById])

  const getLocationLabel = (attrs) => {
    if (attrs.availableAtAllLocations !== false) return 'All locations'
    const ids = Array.isArray(attrs.locationIds) ? attrs.locationIds : []
    if (ids.length === 0) return 'No locations'
    return ids
      .map((id) => locationsById[id]?.name ?? `Location ${id}`)
      .join(', ')
  }

  const handleDeactivate = (row) => {
    const a = getRowAttrs(row)
    if (
      !window.confirm(
        `${a.isActive ? 'Deactivate' : 'Activate'} “${a.name ?? 'this item'}”?`
      )
    ) {
      return
    }
    updateFnbItem(row.id, { isActive: !a.isActive })
      .then(() => load())
      .catch((err) => console.error(err))
  }

  const handleDuplicate = (row) => {
    const a = getRowAttrs(row)
    const payload = {
      organizationId: orgUUID,
      venue_uuid: a.venue_uuid ?? null,
      categoryId: a.categoryId ?? '',
      sku: a.sku ? `${a.sku}-copy` : '',
      name: a.name ? `${a.name} (Copy)` : 'Copy',
      shortName: a.shortName ?? '',
      description: a.description ?? '',
      price: a.price ?? 0,
      cost: a.cost ?? 0,
      taxRate: a.taxRate ?? 0,
      isTaxable: a.isTaxable ?? false,
      isActive: false,
      availableAtAllLocations: a.availableAtAllLocations ?? true,
      locationIds: a.locationIds ?? [],
      availability: a.availability ?? { scope: 'all_events' },
    }
    createFnbItem(payload)
      .then(() => load())
      .catch((err) => console.error(err))
  }

  if (!orgPermissionsReady) return <PageLoadingContainer />
  if (!hasPermission) return <NoPermissionsContainer />
  if (isLoading) return <PageLoadingContainer />

  return (
    <div className="marketplace-layout">
      <div className="marketplace-toolbar">
        <Form.Control
          className="marketplace-search"
          type="search"
          placeholder="Search items…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search items"
        />
        <div className="marketplace-toolbar__actions">
          <Button
            variant="outline-secondary"
            onClick={() =>
              exportItemsCsv(
                items,
                categoriesById,
                locationsById,
                venueNameByUuid
              )
            }
            disabled={items.length === 0}
          >
            Export
          </Button>
          <Button variant="primary" onClick={() => navigate('new')}>
            + Add item
          </Button>
        </div>
      </div>

      {items.length === 0 && !isLoadingData ? (
        <Card body className="card--sm">
          <div className="marketplace-empty">
            <p className="marketplace-empty__message">
              No food &amp; beverage items yet. Add your first item to get
              started.
            </p>
            <Button variant="primary" onClick={() => navigate('new')}>
              + Add item
            </Button>
          </div>
        </Card>
      ) : isLoadingData ? (
        <LoadingContainer />
      ) : (
        <div className="marketplace-table-wrap buyer-types-table-wrap">
          <table className="buyer-types-table">
            <thead>
              <tr>
                <th scope="col">Item name</th>
                <th scope="col">Category</th>
                {requiresVenueSelection ? <th scope="col">Venue</th> : null}
                <th scope="col">Price</th>
                <th scope="col">Cost</th>
                <th scope="col">Margin</th>
                <th scope="col">Status</th>
                <th scope="col">Locations</th>
                <th
                  scope="col"
                  className="buyer-types-table__actions"
                  aria-label="Actions"
                />
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((row) => {
                const a = getRowAttrs(row)
                const margin = calcMargin(a.price, a.cost)
                const imageUrl = resolveMediaUrl(getFnbImageUrl(a))
                return (
                  <tr key={row.id}>
                    <td className="buyer-types-table__name marketplace-item-name-cell">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt=""
                          className="marketplace-item-thumb"
                        />
                      ) : null}
                      <span>{a.name}</span>
                    </td>
                    <td>{categoriesById[a.categoryId]?.name ?? '—'}</td>
                    {requiresVenueSelection ? (
                      <td>
                        {a.venue_uuid
                          ? (venueNameByUuid.get(a.venue_uuid) ?? '—')
                          : 'All venues'}
                      </td>
                    ) : null}
                    <td>{formatCurrency(a.price)}</td>
                    <td>{formatCurrency(a.cost)}</td>
                    <td>{margin}%</td>
                    <td>
                      <span
                        className={`marketplace-status ${
                          a.isActive
                            ? 'marketplace-status--active'
                            : 'marketplace-status--inactive'
                        }`}
                      >
                        {a.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>{getLocationLabel(a)}</td>
                    <td className="buyer-types-table__actions">
                      <Dropdown align="end">
                        <Dropdown.Toggle
                          variant="link"
                          className="buyer-types-table__icon-btn p-0"
                          id={`item-actions-${row.id}`}
                        >
                          ⋮
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() => navigate(`${row.id}/edit`)}
                          >
                            Edit
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => handleDuplicate(row)}>
                            Duplicate
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => handleDeactivate(row)}>
                            {a.isActive ? 'Deactivate' : 'Activate'}
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {filteredItems.length === 0 && (
            <p className="text-muted mt-3 mb-0">No items match your search.</p>
          )}
        </div>
      )}
    </div>
  )
}
